/**
 * 學年(學期)綁定 —— 純邏輯 + 持久化 helper(學習側,單一真相)。
 *
 * boss 2026-06-10「綁定小孩學年,等這次期末考結束就換成四年級課程,除非要複習
 * 才會把舊的拿出來」。本檔集中:
 *   - `loadGradeSemester()` / `setGradeSemester()`:讀寫 `profile.currentGradeSemester`,
 *     走既有 `saveProgress`(localStorage + Supabase 全物件同步),不自建存檔。
 *   - `nextGradeSemester()`:升學年純函式(3.1→3.2→4.1→4.2,4.2 封頂)。
 *   - `shouldShowGradeUpgradeBanner()`:純函式 —「目前學年的期末考日期已過」⇒ 該升。
 *   - `GRADE_SEMESTER_OPTIONS` / `gradeSemesterLabel()`:設定頁下拉 + banner 文案用。
 *
 * 學習/遊戲分離鐵則:升學年**只切 `currentGradeSemester`**,絕不動 factionXP /
 * skillLevels / commanderXp / 題庫內容。「出考卷中心」`listExamUnits` 已用此值過濾,
 * 改完即時反映(練 / 考只當前學年;複習含過去)。
 */

import { loadProgress, saveProgress, createFreshProgress } from '../shared/progress';
import type { GradeSemester, PlayerProgress } from '../shared/types';
import type { ExamEvent } from './exam-plan/types';

/** 沒設 `currentGradeSemester` 的存檔視為三下(3.2)。與 exam-center-route 預設一致。 */
export const DEFAULT_GRADE_SEMESTER: GradeSemester = 3.2;

/** 升學年順序(三上→三下→四上→四下);用於 `nextGradeSemester` 與設定頁排序。 */
export const GRADE_SEMESTER_SEQUENCE: readonly GradeSemester[] = [3.1, 3.2, 4.1, 4.2];

/** 設定頁下拉 / 分段選項(依就讀順序)。 */
export const GRADE_SEMESTER_OPTIONS: ReadonlyArray<{ value: GradeSemester; label: string }> = [
  { value: 3.1, label: '三年級上學期' },
  { value: 3.2, label: '三年級下學期' },
  { value: 4.1, label: '四年級上學期' },
  { value: 4.2, label: '四年級下學期' },
];

/** 學年小數 → 中文顯示(設定頁 / banner 用)。 */
export function gradeSemesterLabel(g: GradeSemester): string {
  return GRADE_SEMESTER_OPTIONS.find((o) => o.value === g)?.label ?? '三年級下學期';
}

/**
 * 純函式:某學年的「下一個學年」。已到四下(4.2,目前課程上限)則回原值(封頂,不繞回)。
 * 不讀存檔、無副作用 —— 方便單測(3.2→4.1 等邊界)。
 */
export function nextGradeSemester(current: GradeSemester): GradeSemester {
  const idx = GRADE_SEMESTER_SEQUENCE.indexOf(current);
  if (idx < 0) return current;
  if (idx >= GRADE_SEMESTER_SEQUENCE.length - 1) return current; // 4.2 封頂
  return GRADE_SEMESTER_SEQUENCE[idx + 1];
}

/**
 * 純函式:從一份 progress 解出當前學年(無 / 不合法 ⇒ 預設三下 3.2)。
 * 與 exam-center.ts 既有 fallback 一致(舊存檔免遷移)。
 */
export function gradeSemesterOf(progress: PlayerProgress | null | undefined): GradeSemester {
  const g = progress?.currentGradeSemester;
  if (g === 3.1 || g === 3.2 || g === 4.1 || g === 4.2) return g;
  return DEFAULT_GRADE_SEMESTER;
}

/** 讀目前存檔的當前學年(包 try/catch;讀不到 ⇒ 預設三下)。 */
export function loadGradeSemester(): GradeSemester {
  try {
    return gradeSemesterOf(loadProgress());
  } catch {
    return DEFAULT_GRADE_SEMESTER;
  }
}

/**
 * 寫入當前學年並持久化(走 `saveProgress` → localStorage + 既有 Supabase / 家庭碼同步)。
 * **只**改 `currentGradeSemester`,不碰任何戰力 / 帳號數值。回傳寫入後的值。
 */
export function setGradeSemester(g: GradeSemester): GradeSemester {
  const p = loadProgress() ?? createFreshProgress('指揮官');
  p.currentGradeSemester = g;
  saveProgress(p);
  return g;
}

/**
 * 升一階學年並持久化(設定頁手動升級鈕 / banner「升上四年級」用)。
 * = `setGradeSemester(nextGradeSemester(目前))`。回傳升級後的值(已封頂則不變)。
 */
export function advanceGradeSemester(): GradeSemester {
  return setGradeSemester(nextGradeSemester(loadGradeSemester()));
}

/**
 * 純函式:判斷「是否該顯示『這學期結束了,要升上四年級嗎?』banner」。
 *
 * 觸發條件(全部成立):
 *   1. 目前學年**不是**課程上限(4.2);已到頂沒有「下一年級」可升。
 *   2. 考試規劃裡有一筆**期末考(type==='final')日期已過**今天(date <= today)。
 *   3. 使用者尚未 dismiss 過「該學年」的這個提示(`dismissedGradeUpgrade` 不含目前學年)。
 *
 * 期末考日期來源 = exam-planner 的 `loadExamPlan()`(已存在,見 exam-plan/store.ts)。
 * 無任何 final 事件 ⇒ 不自動顯示(回 false);此時走設定頁手動升級鈕。
 *
 * @param current   目前學年。
 * @param examPlan  考試規劃事件清單(呼叫端傳 `loadExamPlan()`,便於單測注入)。
 * @param todayISO  今天日期 'YYYY-MM-DD'(便於單測;預設取系統當日本地日期)。
 * @param dismissedGrades 已 dismiss 過提示的學年清單(progress.dismissedGradeUpgrade)。
 */
export function shouldShowGradeUpgradeBanner(
  current: GradeSemester,
  examPlan: ReadonlyArray<Pick<ExamEvent, 'type' | 'date'>>,
  todayISO: string = localTodayISO(),
  dismissedGrades: ReadonlyArray<GradeSemester> = [],
): boolean {
  // 1. 已到課程上限 → 無可升,不顯示。
  if (nextGradeSemester(current) === current) return false;
  // 3. 已 dismiss 過此學年 → 不再煩。
  if (dismissedGrades.includes(current)) return false;
  // 2. 有一筆期末考日期 <= 今天(已過)。
  return examPlan.some((e) => e.type === 'final' && e.date <= todayISO);
}

/** 今天的本地日期 'YYYY-MM-DD'(與 exam-plan 日期格式一致)。 */
export function localTodayISO(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
